import { useEffect, useState } from 'react';

import { oauth } from '../utils';

/**
 * Whether an active client catalog exists for {@link clientId}, via `GET /catalogs?client_id=`.
 *
 * @param clientId — Order/project client UUID.
 *
 * @returns `true` if at least one catalog row exists, `false` after an empty or failed response, or when {@link clientId} is missing.
 */
export default function useClientCatalogPresence(
  clientId: string | null | undefined,
): boolean {
  const [hasCatalog, setHasCatalog] = useState<boolean>(false);

  useEffect(() => {
    if (!clientId) {
      setHasCatalog(false);
      return undefined;
    }

    let cancelled = false;
    setHasCatalog(false);

    (async () => {
      try {
        const { json } = await oauth('GET', 'catalogs', { client_id: clientId, page_limit: 1 });
        if (cancelled) {
          return;
        }
        const rows = json?.data as { catalog_id?: string }[] | undefined;
        const has =
          Array.isArray(rows) &&
          rows.length > 0 &&
          Boolean(rows[0]?.catalog_id);
        setHasCatalog(has);
      } catch {
        if (!cancelled) {
          setHasCatalog(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  return hasCatalog;
}
